import { useEffect } from 'react';
import AuthService from '../service/AuthService';
import type { AuthContextProps } from '../Auth.types';
import { isAllowedAccountType } from '../utils/isAllowedAccountType';

/**
 * Initializes `AuthProvider` and `AuthContext`
 * @memberof AuthProvider
 */
const useAuthInit = (props: AuthContextProps): void => {
  const { logout, dispatch } = props;

  /** Try to load user from `authStorage` on page load */
  useEffect(() => {
    const loadUser = async () => {
      try {
        // Get tokens
        let token = '';
        const { accessToken, refreshToken, csrfToken } = AuthService.getTokens();

        if (!accessToken) {
          throw new Error('Could not load user: no access token stored.');
        }
        if (!refreshToken) {
          throw new Error('Could not load user: no refresh token stored.');
        }
        /**
         * Don't logout user if token is missing, make new CSRF instead
         */
        if (!csrfToken) {
          const { csrf_token } = await AuthService.CSRFToken();
          token = csrf_token;
        } else {
          token = csrfToken;
        }

        // Get user
        const user = await AuthService.getUser({ accessToken });

        if (!isAllowedAccountType(user.account_type)) {
          AuthService.clearTokens();
          throw new Error('Access denied. This portal is for B2B clients only.');
        }

        // Set user
        dispatch({
          type: 'LOGIN',
          payload: {
            user,
            accessToken,
            refreshToken,
            csrfToken: token,
          },
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        // Set state to loggedout since operation failed.
        // Using this `logout` method will notify current and other tabs of a logout.
        await logout({});
      }
    };

    dispatch({ type: 'SET_LOADING', payload: { loading: true } });
    loadUser().then();
  }, []);
};

export default useAuthInit;
